import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, BarChart2 } from "lucide-react";
import { useTorneo, useTorneoTablaPosiciones, useTorneoCalendario } from "../hooks/useTorneos";
import TablaCategoriaTorneo from "../components/categorias/TablaCategoriaTorneo";

type Tab = "tabla" | "calendario";

export default function TorneoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("tabla");

  const { data: torneo, isLoading } = useTorneo(id!);
  const { data: tabla = [] } = useTorneoTablaPosiciones(id!);
  const { data: partidos = [] } = useTorneoCalendario(id!);

  if (isLoading) return <div className="p-8 text-center text-gray-400">Cargando...</div>;
  if (!torneo) return <div className="p-8 text-center text-red-400">Torneo no encontrado</div>;

  const resultadoColor = (gf: number, gc: number) => {
    if (gf > gc) return "text-green-600 font-bold";
    if (gf < gc) return "text-red-500 font-bold";
    return "text-gray-500";
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate("/torneos")} className="text-gray-400 hover:text-gray-700">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{torneo.nombre}</h1>
          <p className="text-gray-500 text-sm">
            {torneo.organizador && `${torneo.organizador} · `}
            <span className="capitalize">{torneo.tipo ?? "torneo"}</span>
            {torneo.fecha_inicio && ` · desde ${new Date(torneo.fecha_inicio).toLocaleDateString("es-CO")}`}
          </p>
        </div>
        <span className={`ml-auto text-xs px-3 py-1 rounded-full font-medium ${
          torneo.estado === "activo" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
        }`}>
          {torneo.estado}
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 w-fit">
        {([
          { key: "tabla", label: "Tabla de posiciones", icon: <BarChart2 className="w-4 h-4" /> },
          { key: "calendario", label: `Calendario (${partidos.length})`, icon: <Calendar className="w-4 h-4" /> },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? "bg-white text-blue-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === "tabla" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <TablaCategoriaTorneo filas={tabla as any[]} mostrarNombre />
        </div>
      )}

      {tab === "calendario" && (
        <div className="space-y-3">
          {(partidos as any[]).map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">vs {p.rival}</p>
                <p className="text-xs text-gray-500">{p.sede ?? "Sede por confirmar"}</p>
              </div>
              <div className="text-right">
                {p.goles_favor !== null && p.goles_contra !== null ? (
                  <p className={`text-lg ${resultadoColor(p.goles_favor, p.goles_contra)}`}>
                    {p.goles_favor} - {p.goles_contra}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400">Pendiente</p>
                )}
                <p className="text-xs text-gray-400">{new Date(p.fecha).toLocaleDateString("es-CO")}</p>
              </div>
            </div>
          ))}
          {partidos.length === 0 && (
            <div className="text-center py-12 text-gray-400">Sin partidos registrados</div>
          )}
        </div>
      )}
    </div>
  );
}
